import React, { useEffect, useMemo, useState } from "react";

import { Spinner } from "@performyard/py-components";

import EmptyChart from "../../../../../../components/EmptyChart";
// components
import Text from "../../../../../../components/Text";
import { CardBody, CardHeader } from "../../../../components/Card";
import HeatmapTable, { IColumnSort, IHeatmapColumn } from "../../../../components/HeatmapTable";
import YearAndCohortDropdowns from "./YearAndCohortDropdowns";

// store
import useStore from "../../../../store";

// api hooks
import {
	useCohorts,
	useCustomFactorsIncludedInSatisfaction,
	useFactorsSummary,
	usePageSettings,
	useUpdatePageSettings,
} from "../../../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../../../hooks/useFeatureFlag";

// utils
import downloadCSV from "../../../../../../utils/downloadCSV";

// config
import Config, { defaultSelectColor } from "../../../../Config";
// css
import css from "./CalendarFactorsHeatmap.module.scss";

/**
 * Heatmap of factors over time
 */
const CalendarFactorsHeatmap = () => {
	// setup
	const { value: customFactorsEnabled } = useFeatureFlagQuery("ff-engagement-custom-factors", false);
	const { data: customFactorsData } = useCustomFactorsIncludedInSatisfaction({ enabled: customFactorsEnabled });
	const factors = useMemo(() => {
		const customFactorNames = customFactorsEnabled
			? customFactorsData?.customFactors?.map((factor) => factor.name) || []
			: [];
		return [...Config.FACTORS.FACTORS, ...customFactorNames].sort();
	}, [customFactorsEnabled, customFactorsData]);
	const [sortedFactors, setSortedFactors] = useState<string[]>([
		"Overall Satisfaction",
		...Config.FACTORS.FACTORS.sort(),
	]);
	const [sortedColumn, setSortedColumn] = useState<IColumnSort>({ columnId: "factors", direction: "asc" });

	// Update sorted factors when custom factors load
	useEffect(() => {
		setSortedFactors(["Overall Satisfaction", ...factors]);
	}, [factors]);
	const [filter] = useStore((state) => [state.filter]);
	const { data: pageSettings } = usePageSettings();
	const { mutate: updatePageSettings } = useUpdatePageSettings();
	const { data: cohortMetaData } = useCohorts();
	const { data: oldestFactorDate } = useFactorsSummary({
		binSize: Config.BIN_SIZE,
	});

	const getYear = () => {
		return new Date().getFullYear();
	};

	// get all year options for dropdown
	// if overall filter is selected, years options depends on oldest factor date
	// otherwise, it will depend on the start date (year) of the filter
	const years = useMemo(() => {
		const oldestDate =
			oldestFactorDate?.data.reverse()[0]?.time_bucket?.getFullYear() || new Date().getFullYear() - 1;
		const startDate = filter.startDate ? filter.startDate.getFullYear() : oldestDate;
		const endDate = filter.endDate ? filter.endDate.getFullYear() : new Date().getFullYear();

		const possibleYears: number[] = [];
		for (let i = startDate; i <= endDate; i++) {
			possibleYears.push(i);
		}
		return possibleYears;
	}, [oldestFactorDate, filter.startDate, filter.endDate]);

	// get current year from page settings, or default to current year
	const year = pageSettings?.factors?.heatmap?.year ? parseInt(pageSettings?.factors?.heatmap?.year) : getYear();

	const cohortIds = cohortMetaData?.cohorts.find((coho) => pageSettings?.factors?.heatmap?.cohort === coho._id)
		? pageSettings?.factors.heatmap.cohort
		: undefined;

	// get data for heatmap from our api
	const {
		data: factorsData,
		isLoading: isFactorsLoading,
		error: factorsError,
	} = useFactorsSummary({
		startDate: filter.startDate,
		endDate: filter.endDate,
		binSize: Config.BIN_SIZE,
		cohortIds,
	});

	// when filter changes, we may want to switch the year filter
	useEffect(() => {
		// see all years for which there is data
		const dataYears = factorsData?.data.map((d) => d.time_bucket?.getFullYear().toString()) || [];
		// if the current year is not in the list of years for which there is data, switch to the closest year with data
		if (dataYears.length && !dataYears.includes(year.toString())) {
			const closestYear = dataYears.reduce((prev, curr) => {
				if (prev && curr) {
					return Math.abs(parseInt(curr) - year) <= Math.abs(parseInt(prev) - year) ? curr : prev;
				}
			}, dataYears[0]);
			updatePageSettings({
				settings_path: "factors.heatmap.year",
				settings_value: closestYear || "",
			});
		}
	}, [factorsData]);

	const sortingChanged = (newRowIds: string[], newColumn: IColumnSort) => {
		setSortedFactors(newRowIds);
		setSortedColumn(newColumn);
	};

	const monthColumns: IHeatmapColumn[] = useMemo(() => {
		const emptyFactorsMap = ["Overall Satisfaction", ...factors].reduce((accum, curr) => {
			return { ...accum, [curr]: null };
		}, {});
		const selectMonthlyFactorData = (monthIndex: number) => {
			const monthlyFactorsData =
				factorsData?.data.reduce(
					(acc, factor_point) => {
						// only include this month for selected year, only include factors listed in the factors array
						if (
							factor_point.time_bucket?.getMonth() === monthIndex &&
							factor_point.time_bucket.getFullYear() === year &&
							factors.includes(factor_point.factor) // filters out eNPS and "Overall Satisfaction"
						) {
							acc[factor_point.factor] =
								factor_point.factor && factor_point.average
									? parseFloat(factor_point.average.toFixed(1))
									: null;
						}
						return acc;
					},
					{ ...emptyFactorsMap }
				) || emptyFactorsMap;
			// total for all factors for this month
			const totalAllFactorsForMonth = factors.reduce((acc, factor) => {
				return acc ? acc + monthlyFactorsData[factor] : monthlyFactorsData[factor];
			}, null);
			// length of factors having data
			const numFactorsWithData = factors.reduce((acc, factor) => {
				return acc + (monthlyFactorsData[factor] ? 1 : 0);
			}, 0);
			// add overall satisfaction to the list of factors (or null if all null)
			monthlyFactorsData["Overall Satisfaction"] =
				totalAllFactorsForMonth && numFactorsWithData
					? parseFloat((totalAllFactorsForMonth / numFactorsWithData).toFixed(1))
					: null;
			return monthlyFactorsData;
		};

		const monthsOfTheYearMap = {
			...Object.fromEntries(
				Array(12)
					.fill(0)
					.map((_, monthIndex) => [
						new Date(year, monthIndex, 1).toLocaleString("default", { month: "long" }),
						{
							columnId: new Date(year, monthIndex, 1).toISOString(),
							label: new Date(year, monthIndex, 1).toLocaleString("default", { month: "long" }),
							data: factorsData ? selectMonthlyFactorData(monthIndex) : {},
						},
					])
			),
		};
		return Object.values(monthsOfTheYearMap);
	}, [factorsData, year]);

	const getCSVContent = (data: IHeatmapColumn[]) => {
		const csvColumns = ["Factors", ...data.map((d) => `"${d.label}"`)].join(",");

		const csvRows = sortedFactors
			.map((key) => {
				const row = [key, ...data.map((d) => `"${d.data[key] || ""}"`)].join(",");
				return row;
			})
			.join("\n");

		const csvContent = `${csvColumns}\n${csvRows}`;

		return csvContent;
	};

	const selectCSVDownloadName = () => {
		const date = new Date().toDateString();

		const cohort_id = pageSettings?.factors?.heatmap?.cohort || null;

		const cohortData = cohortMetaData?.cohorts.find((c) => c._id === cohort_id);

		const cohortName = cohortData ? cohortData.name : "All Employees";

		const year = pageSettings?.factors?.heatmap?.year
			? parseInt(pageSettings.factors.heatmap.year)
			: Config.FACTORS.HEATMAP.DEFAULT_YEAR_SELECTION;

		return `PerformYard-Engagement-Factors-${year}-${cohortName}-${date}.csv`;
	};

	const hasData = factorsData?.data.length && factorsData?.data.length > 0;

	return (
		<>
			<CardHeader
				title={
					<span>
						<Text element="span" size="paragraph-2" weight="semi-bold">
							Satisfaction Factors
						</Text>
					</span>
				}
				toolBar={<YearAndCohortDropdowns years={years} />}
				info="The Satisfaction Factors Heatmap Table displays monthly satisfaction and engagement factor scores for the selected calendar year and cohort."
				options={
					hasData // only allow Download CSV when chart has data
						? [
								{
									id: "download-csv",
									label: "Download CSV",
									action: () => {
										const csvContent = getCSVContent(monthColumns);
										const filename = selectCSVDownloadName();
										downloadCSV(csvContent, filename);
									},
								},
							]
						: undefined
				}
			/>
			<CardBody>
				<div className={css.cardBody}>
					{isFactorsLoading ? (
						<div className={css.emptyChart}>
							<Spinner size={20} />
						</div>
					) : (
						<>
							{!isFactorsLoading && !hasData ? (
								<div className={css.emptyChart}>
									<EmptyChart type="heatmap" />
								</div>
							) : (
								<div>
									<HeatmapTable
										rowIds={isFactorsLoading ? [] : sortedFactors}
										// keeps the "Overall Satisfaction" row at the top of the table
										frozenTopRow={true}
										// columns
										columns={[
											{
												columnId: "factors",
												label: "Factors",
												minWidth: "184px",
												alignLeft: true,
												data: {
													"Overall Satisfaction": "Overall Satisfaction",
													...sortedFactors.reduce((acc, factor) => {
														acc[factor] = factor;
														return acc;
													}, {}),
												},
												selectBackgroundColor: () => "", // no color
											},
											...monthColumns,
										]}
										// the color function to use for the cell, creates the heatmap effect
										defaultSelectBackgroundColor={defaultSelectColor}
										sortedColumn={sortedColumn}
										onSortingChanged={sortingChanged}
									/>
									{factorsError && (
										<Text size="paragraph-3" weight="regular">
											Error fetching factors
										</Text>
									)}
								</div>
							)}
						</>
					)}
				</div>
			</CardBody>
		</>
	);
};

export default CalendarFactorsHeatmap;
